(() => {
  const labels = document.currentScript?.dataset;
  const shareLabel = labels?.shareLabel;
  const mobile = window.matchMedia('(max-width: 749px)');
  const initialized = new WeakSet();

  function enhance(rail) {
    if (initialized.has(rail) || !shareLabel) return;
    const originalToggle = rail.querySelector(':scope > .rawa-button');
    const options = rail.querySelector(':scope > .rawa-buttons');
    const items = options ? Array.from(options.querySelectorAll('li[data-type]')) : [];
    const icon = originalToggle?.querySelector('.rawa-svg--share');
    if (!originalToggle || !options || !items.length || !icon) return;
    initialized.add(rail);

    const saved = new Map();
    const remember = (element, names) => saved.set(element, names.map(name => [name, element.getAttribute(name)]));
    remember(options, ['id', 'role', 'aria-label', 'aria-hidden']);
    items.forEach(item => remember(item, ['role', 'tabindex', 'aria-label']));
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'bb-mobile-share-toggle';
    toggle.setAttribute('aria-label', shareLabel);
    toggle.setAttribute('aria-expanded', 'false');
    const toggleIcon = icon.cloneNode(true);
    toggleIcon.setAttribute('aria-hidden', 'true');
    toggle.append(toggleIcon);
    rail.insertBefore(toggle, options);

    function setOpen(open, returnFocus = false) {
      rail.dataset.bbShareOpen = String(open);
      toggle.setAttribute('aria-expanded', String(open));
      options.setAttribute('aria-hidden', String(!open));
      items.forEach(item => item.setAttribute('tabindex', open ? '0' : '-1'));
      if (returnFocus) toggle.focus();
    }

    function updateMode() {
      if (mobile.matches) {
        options.id = 'bb-mobile-share-options';
        options.setAttribute('role', 'group');
        options.setAttribute('aria-label', shareLabel);
        toggle.setAttribute('aria-controls', options.id);
        items.forEach(item => {
          item.setAttribute('role', 'button');
          item.setAttribute('aria-label', item.dataset.tooltip || item.dataset.type);
        });
        rail.classList.add('bb-mobile-share');
        setOpen(false);
      } else {
        rail.classList.remove('bb-mobile-share');
        delete rail.dataset.bbShareOpen;
        toggle.removeAttribute('aria-controls');
        toggle.setAttribute('aria-expanded', 'false');
        saved.forEach((attributes, element) => attributes.forEach(([name, value]) => {
          if (value === null) element.removeAttribute(name);
          else element.setAttribute(name, value);
        }));
      }
    }

    toggle.addEventListener('click', event => {
      if (!mobile.matches) return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });
    toggle.addEventListener('keydown', event => {
      if (!mobile.matches || event.key !== 'ArrowDown') return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
      items[0].focus();
    });
    items.forEach(item => {
      item.addEventListener('keydown', event => {
        if (!mobile.matches || !['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        item.click();
        setOpen(false, true);
      });
      item.addEventListener('click', () => {
        if (mobile.matches) setOpen(false);
      }, true);
    });
    options.addEventListener('click', event => {
      if (mobile.matches && event.target === options) event.stopPropagation();
    });
    rail.addEventListener('keydown', event => {
      if (!mobile.matches || event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false, true);
    });
    rail.addEventListener('focusout', event => {
      if (mobile.matches && !rail.contains(event.relatedTarget)) setOpen(false);
    });
    document.addEventListener('pointerdown', event => {
      if (mobile.matches && !rail.contains(event.target)) setOpen(false);
    }, true);
    mobile.addEventListener('change', updateMode);
    updateMode();
  }

  function enhanceChat(widget) {
    if (initialized.has(widget) || !labels?.chatLabel || !labels?.closeLabel) return;
    const opener = widget.querySelector('#rawa-button');
    const close = widget.querySelector('#rawa-close');
    const box = widget.querySelector('#rawa-box');
    if (!opener || !close || !box) return;
    initialized.add(widget);
    const names = ['role', 'tabindex', 'aria-label', 'aria-expanded', 'aria-controls'];
    const saved = [opener, close].map(element => [element, names.map(name => [name, element.getAttribute(name)])]);
    const originalInert = box.hasAttribute('inert');
    function updateChat() {
      if (mobile.matches) {
        const open = widget.classList.contains('rawa-opened');
        if (!open && box.contains(document.activeElement)) opener.focus();
        box.toggleAttribute('inert', !open);
        opener.setAttribute('role', 'button');
        opener.setAttribute('aria-label', labels.chatLabel);
        opener.setAttribute('aria-controls', box.id);
        opener.setAttribute('aria-expanded', String(open));
        opener.setAttribute('tabindex', open ? '-1' : '0');
        close.setAttribute('role', 'button');
        close.setAttribute('aria-label', labels.closeLabel);
        close.setAttribute('tabindex', open ? '0' : '-1');
      } else {
        box.toggleAttribute('inert', originalInert);
        saved.forEach(([element, attributes]) => attributes.forEach(([name, value]) => {
          if (value === null) element.removeAttribute(name);
          else element.setAttribute(name, value);
        }));
      }
    }
    [opener, close].forEach(element => element.addEventListener('keydown', event => {
      if (!mobile.matches || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      element.click();
      if (element === opener) close.focus();
      else opener.focus();
    }));
    document.addEventListener('focusin', event => {
      if (mobile.matches && !widget.classList.contains('rawa-opened') && box.contains(event.target)) opener.focus();
    }, true);
    widget.addEventListener('keydown', event => {
      if (!mobile.matches || event.key !== 'Escape' || !widget.classList.contains('rawa-opened')) return;
      event.preventDefault();
      event.stopPropagation();
      close.click();
      opener.focus();
    });
    new MutationObserver(updateChat).observe(widget, { attributes: true, attributeFilter: ['class'] });
    mobile.addEventListener('change', updateChat);
    updateChat();
  }

  function discover() {
    const rail = document.getElementById('rawa-share');
    if (rail) enhance(rail);
    const widget = document.getElementById('rawa-widget');
    if (widget) enhanceChat(widget);
  }
  discover();
  new MutationObserver(discover).observe(document.body, { childList: true, subtree: true });
})();
